import React from 'react'
import {Dialog, DialogTitle, DialogActions, Button} from '@mui/material'
import {keyBoardPressActions} from '@oc/utils'
import {messages} from './texts'

export type Confirm = (message: string, yes?: string, no?: string) => Promise<boolean>

type ResolveConfirm = (value: boolean) => void

interface ConfirmContext {
    yesText: string
    noText: string
    currentMessage: string
    resolveMessage?: ResolveConfirm
    confirm: Confirm
}

const ConfirmContext = React.createContext<ConfirmContext>({
    currentMessage: '',
    yesText: 'Yes',
    noText: 'No',
    confirm: () => Promise.reject(new Error('ConfirmContext not initialized'))
})

export const useConfirm = (): Confirm => React.useContext(ConfirmContext).confirm

export const ConfirmContextProvider = ({children}: React.PropsWithChildren) => {
    const [yesText, setYesText] = React.useState(() => messages.getText('confirmPrompt', 'yes'))
    const [noText, setNoText] = React.useState(() => messages.getText('confirmPrompt', 'no'))
    const [[currentMessage, resolveMessage], setCurrentMessageAndResolve] = React.useState<
        [string, ResolveConfirm | undefined]
    >(['', undefined])

    const confirm: Confirm = React.useCallback(
        (message: string, yes?: string, no?: string) => {
            if (resolveMessage) {
                throw messages.getError('multipleConfirms')
            }

            let newResolveMessage = undefined as any as ResolveConfirm
            const resolvePromise = new Promise<boolean>(resolve => {
                newResolveMessage = (value: boolean) => {
                    setCurrentMessageAndResolve(([msg]) => [msg, undefined])
                    resolve(value)
                }
            })

            setCurrentMessageAndResolve([message, newResolveMessage])
            setYesText(() => yes || messages.getText('confirmPrompt', 'yes'))
            setNoText(() => no || messages.getText('confirmPrompt', 'no'))

            return resolvePromise
        },
        [resolveMessage]
    )

    const memoValue: ConfirmContext = React.useMemo(
        () => ({confirm, currentMessage, resolveMessage, yesText, noText}),
        [confirm, currentMessage, resolveMessage, yesText, noText]
    )

    return <ConfirmContext.Provider value={memoValue}>{children}</ConfirmContext.Provider>
}

export const ConfirmPrompt = () => {
    const {currentMessage, yesText, noText, resolveMessage} = React.useContext(ConfirmContext)

    const answerYes = React.useCallback(() => {
        resolveMessage && resolveMessage(true)
    }, [resolveMessage])
    const answerNo = React.useCallback(() => {
        resolveMessage && resolveMessage(false)
    }, [resolveMessage])

    return (
        <Dialog
            onKeyUp={event =>
                keyBoardPressActions(
                    event,
                    answerNo,
                    answerYes,
                    undefined,
                    undefined,
                    !!currentMessage && !!resolveMessage
                )
            }
            keepMounted
            open={!!currentMessage && !!resolveMessage}
            id="confirmDialog"
        >
            <DialogTitle>{currentMessage}</DialogTitle>
            <DialogActions>
                <Button onClick={answerNo}>{noText}</Button>
                <Button color="primary" onClick={answerYes}>
                    {yesText}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
